import { abbreviate, formatMoney } from '../format.ts';
import type { BookingView, ChallengeView, DemoConfig, Session } from '../types.ts';

export interface AuthState {
  readonly stepUp:
    'not required' | 'required' | 'in progress' | 'verified' | 'locked' | 'expired' | 'changed';
  readonly transactionId?: string | undefined;
  readonly attemptsRemaining?: number | undefined;
  readonly secondsRemaining?: number | undefined;
  readonly grantHeld: boolean;
  readonly grantSecondsRemaining?: number | undefined;
  readonly grantSpent: boolean;
}

interface StatePanelsProps {
  readonly session: Session;
  readonly config: DemoConfig;
  readonly booking: BookingView | undefined;
  readonly challenge: ChallengeView | undefined;
  readonly auth: AuthState;
}

/**
 * Two readouts, side by side, for the whole point of the demo.
 *
 * Business state and authentication state are separate panels because they are separate
 * facts that are allowed to disagree, and every interesting moment in this system is a
 * moment where they do: the booking is still a draft while a grant exists; the grant is
 * held while the content has moved; the customer is signed in while step-up is
 * outstanding. Merging them into one "status" would erase the distinction the reference
 * implementation exists to demonstrate.
 *
 * Nothing here is computed in the browser. Every value is what the server last said.
 */
export function StatePanels({ session, config, booking, challenge, auth }: StatePanelsProps) {
  return (
    <>
      <section className="readout" aria-labelledby="business-state">
        <p className="eyebrow" id="business-state">
          Business state
        </p>
        <dl className="readout__rows">
          <dt>booking</dt>
          <dd>{booking ? booking.bookingId : '\u2014'}</dd>

          <dt>status</dt>
          <dd>
            <span
              className={`state ${booking?.status === 'CONFIRMED' ? 'state--sealed' : 'state--idle'}`}
            >
              {booking?.status ?? '\u2014'}
            </span>
          </dd>

          <dt>amount</dt>
          <dd>{booking ? formatMoney(booking.amountMinor, booking.currency) : '\u2014'}</dd>

          <dt>threshold</dt>
          <dd>{booking ? formatMoney(booking.thresholdMinor, booking.currency) : '\u2014'}</dd>

          <dt>version</dt>
          <dd>{booking ? booking.version : '\u2014'}</dd>

          <dt>content hash</dt>
          <dd>{booking ? booking.contentHashPrefix : '\u2014'}</dd>
        </dl>
      </section>

      <section className="readout" aria-labelledby="auth-state">
        <p className="eyebrow" id="auth-state">
          Authentication state
        </p>
        <dl className="readout__rows">
          <dt>signed in</dt>
          <dd>
            <span className="state state--sealed">{session.maskedEmail}</span>
          </dd>

          <dt>loyalty</dt>
          <dd>{session.loyaltyTier ?? '\u2014'}</dd>

          <dt>step-up</dt>
          <dd>
            {/*
              A stable handle, for the same reason the booking cards have one: the labels here
              are single ordinary words, and `locked` also appears in the audit trail beside
              this panel as part of `CHALLENGE_LOCKED`. A spec matching this row by its text
              is really matching two unrelated things that happen to share a substring.
            */}
            <span className={`state ${stepUpClass(auth.stepUp)}`} data-testid="auth-step-up">
              {auth.stepUp}
            </span>
          </dd>

          <dt>transaction</dt>
          <dd>{auth.transactionId ? abbreviate(auth.transactionId, 14) : '\u2014'}</dd>

          <dt>attempts left</dt>
          <dd>
            {auth.attemptsRemaining === undefined
              ? '\u2014'
              : `${auth.attemptsRemaining} of ${config.otpMaxAttempts}`}
          </dd>

          <dt>code expires</dt>
          <dd>
            {challenge && auth.secondsRemaining !== undefined
              ? `${Math.max(0, auth.secondsRemaining)}s`
              : '\u2014'}
          </dd>

          <dt>grant</dt>
          <dd>
            <span className={`state ${grantClass(auth)}`}>{grantLabel(auth)}</span>
          </dd>
        </dl>
        {/*
          Stated rather than implied. An audience will assume the browser is holding a
          Cognito session somewhere, because in most SPAs it would be.
        */}
        <p className="muted" style={{ marginTop: '0.75rem', fontSize: '0.75rem' }}>
          This browser holds no Cognito session and no token. The session cookie is httpOnly, so
          this page cannot read it either.
        </p>
      </section>
    </>
  );
}

function stepUpClass(state: AuthState['stepUp']): string {
  if (state === 'verified') return 'state--sealed';
  if (state === 'locked' || state === 'expired' || state === 'changed') return 'state--refused';
  if (state === 'required' || state === 'in progress') return 'state--signal';
  return 'state--idle';
}

function grantClass(auth: AuthState): string {
  if (auth.grantSpent) return 'state--sealed';
  if (auth.grantHeld) return 'state--signal';
  return 'state--idle';
}

function grantLabel(auth: AuthState): string {
  if (auth.grantSpent) return 'consumed';
  if (auth.grantHeld) {
    const remaining = auth.grantSecondsRemaining;
    return remaining === undefined ? 'held' : `held · ${Math.max(0, remaining)}s`;
  }
  return '\u2014';
}
